import { useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Check, Link2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { copyPublicUrl } from '@/utils/copy-public-url'

type CopyPublicLinkButtonProps = {
  url: string
  label?: string
  variant?: 'default' | 'secondary' | 'outline' | 'ghost' | 'ghostToolbar'
  size?: 'default' | 'sm' | 'iconSm'
  /** Render icon-only at `iconSm` with the label shown on hover (tooltip) and to screen readers. */
  iconOnly?: boolean
  disabled?: boolean
  pressFeedback?: boolean
}

const labeledHitAreaClassName =
  'relative pl-2.5 pr-3 after:absolute after:left-1/2 after:top-1/2 after:size-11 after:-translate-x-1/2 after:-translate-y-1/2 after:content-[""]'

export function CopyPublicLinkButton({
  url,
  label = 'Copy link',
  variant = 'outline',
  size = 'sm',
  iconOnly = false,
  disabled = false,
  pressFeedback = true,
}: CopyPublicLinkButtonProps) {
  const [copied, setCopied] = useState(false)
  const effectiveSize = iconOnly ? 'iconSm' : size
  const reducedMotion = useReducedMotion()

  async function handleCopy() {
    const ok = await copyPublicUrl(url)
    if (!ok) return
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2000)
  }

  const icon = copied ? (
    <Check className="h-4 w-4" aria-hidden />
  ) : (
    <Link2 className="h-4 w-4" aria-hidden />
  )

  const button = (
    <Button
      type="button"
      variant={iconOnly ? 'ghostToolbar' : variant}
      size={effectiveSize}
      disabled={disabled}
      pressFeedback={pressFeedback && !iconOnly}
      className={cn(!iconOnly && labeledHitAreaClassName)}
      onClick={() => void handleCopy()}
    >
      {reducedMotion ? (
        icon
      ) : (
        <AnimatePresence initial={false} mode="popLayout">
          <motion.span
            key={copied ? 'copied' : 'copy'}
            className="inline-flex"
            initial={{ opacity: 0, scale: 0.25, filter: 'blur(4px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, scale: 0.25, filter: 'blur(4px)' }}
            transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
          >
            {icon}
          </motion.span>
        </AnimatePresence>
      )}
      {effectiveSize !== 'iconSm' ? label : <span className="sr-only">{label}</span>}
    </Button>
  )

  if (!iconOnly) return button

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{copied ? 'Link copied' : label}</TooltipContent>
    </Tooltip>
  )
}
